import { Button, ToastProvider, TopNav, type NavItem } from "@vidario/ui";
import { useAppBuild } from "@vidario/api-client";
import { appVersionLabel, userInitials } from "@vidario/domain";
import { Briefcase, Building2, LineChart, Settings } from "lucide-react";
import { useEffect, useMemo, type ReactElement } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";

import { tagAppVersion } from "./platform/observability";
import { useAuth } from "./providers/auth-provider";
import { AnonymBar, AnonymToggle } from "./anonym/AnonymToggle";
import { WhatsNew } from "./whats-new/WhatsNew";

/** Layout for every authenticated screen. Top nav is persistent; each
 * route renders into the <Outlet />. Phase 4 fills in the feature screens;
 * this layout stays as-is. */
export function AppLayout(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userError, retryUser, signOut } = useAuth();
  const { data: build } = useAppBuild();
  const versionLabel = build && appVersionLabel(build, import.meta.env.VITE_GLITCHTIP_ENV);

  useEffect(() => {
    if (build?.version) tagAppVersion(build.version);
  }, [build?.version]);

  const isAdmin = user?.role === "admin";

  const items = useMemo<NavItem[]>(() => {
    const active = location.pathname;
    const go = (path: string) => async () => {
      await navigate(path);
    };
    return [
      {
        id: "dashboard",
        label: "Jobs",
        icon: Briefcase,
        active: active === "/dashboard" || active.startsWith("/jobs"),
        onSelect: go("/dashboard"),
      },
      // Controlling and Firmenklassen are admin-only.
      ...(isAdmin
        ? [
            {
              id: "controlling",
              label: "Controlling",
              icon: LineChart,
              active: active === "/controlling",
              onSelect: go("/controlling"),
            } satisfies NavItem,
            {
              id: "company-classes",
              label: "Firmenklassen",
              icon: Building2,
              active: active === "/firmenklassen",
              onSelect: go("/firmenklassen"),
            } satisfies NavItem,
          ]
        : []),
      {
        id: "settings",
        label: "Einstellungen",
        icon: Settings,
        active: active === "/settings",
        inUserMenu: true,
        onSelect: go("/settings"),
      },
    ];
  }, [location.pathname, navigate, isAdmin]);

  // Never a blank page while `/me` loads or fails (#23). An expired session
  // doesn't land here: it sends the user to login instead.
  if (!user) {
    if (!userError) {
      return (
        <div
          className="flex min-h-screen items-center justify-center bg-bg-app text-sm text-text-tertiary"
          data-testid="auth-loading"
        >
          Sitzung wird geprüft…
        </div>
      );
    }
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-app px-4">
        <div
          role="alert"
          className="w-full max-w-md rounded-lg border border-border-default bg-neutral-0 p-8 text-center shadow-sm"
        >
          <h1 className="m-0 text-lg font-semibold">Dein Profil konnte nicht geladen werden.</h1>
          <p className="mt-2 text-sm text-text-secondary">
            Bitte versuche es erneut. Wenn das nicht hilft, melde dich ab und wieder an.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="secondary" onClick={() => void signOut()}>
              Abmelden
            </Button>
            <Button onClick={retryUser}>Erneut versuchen</Button>
          </div>
        </div>
      </main>
    );
  }

  const navUser = {
    name: user.name,
    email: user.email,
    initials: userInitials(user.name),
  };

  return (
    <ToastProvider>
      <TopNav
        items={items}
        user={navUser}
        userRole={user.role}
        actions={
          <>
            {isAdmin && <AnonymToggle />}
            <WhatsNew key={user.id} user={user} />
          </>
        }
        banner={<AnonymBar />}
        onLogoClick={() => void navigate("/dashboard")}
        onSettings={() => void navigate("/settings")}
        {...(versionLabel && { versionLabel })}
        {...(import.meta.env.DEV && {
          onDesignSystem: () => void navigate("/foundations"),
        })}
        onLogout={() => {
          void (async () => {
            await signOut();
            await navigate("/login", { replace: true });
          })();
        }}
      />
      <Outlet />
    </ToastProvider>
  );
}
